import { Component, Input, OnInit, OnChanges, ChangeDetectorRef, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-pdf-viewer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="h-full w-full" #container>
      @if(pdfUrl && !isLoading) {
        <div class="relative h-full w-full">
          <iframe 
            #pdfIframe
            [src]="safePdfUrl" 
            class="h-full w-full border-0"
            [class.hidden]="isResizing"
            [title]="title"
            frameborder="0"
            allowfullscreen>
          </iframe>
          @if(isResizing) {
            <div class="absolute inset-0 flex items-center justify-center bg-white">
              <div class="text-sm text-gray-600">Resizing…</div>
            </div>
          }
        </div>
      } @else {
        <div class="flex items-center justify-center h-full">
          <div class="text-center">
            <i class="pi pi-file-pdf text-4xl text-gray-400"></i>
            <p class="text-gray-600">{{ pdfUrl ? 'Loading PDF...' : 'No PDF to display' }}</p>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
      width: 100%;
    }
  `]
})
export class PdfViewerComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('container', { static: true }) container!: ElementRef;
  @ViewChild('pdfIframe') pdfIframe!: ElementRef;
  private _pdfUrl: string = '';
  @Input() set pdfUrl(url: string) {
    this._pdfUrl = url;
    this.updatePdfUrl();
  }
  get pdfUrl(): string {
    return this._pdfUrl;
  }

  private _initialPage: number = 1;
  @Input() set initialPage(page: number) {
    this._initialPage = page;
    this.updatePdfUrl();
  }
  get initialPage(): number {
    return this._initialPage;
  }

  _title: string = 'PDF Document';
  @Input() set title(title: string) {
    this._title = title;
    this.updatePdfUrl();
  }
  get title(): string {
    return this._title;
  }

  @Input() hideToolbar: boolean = false; // New configurable option
  @Input() hideNavPanes: boolean = true; // Hide side navigation panes
  @Input() fitToWidth: boolean = true; // Fit to page width
  /**
   * When true, we avoid expensive PDF reflow during container resizing by temporarily hiding the iframe.
   * This keeps live panel resizing smooth, and the PDF snaps to the new size when resizing ends.
   */
  @Input() isResizing: boolean = false;

  pdfUrlWithPage: string = '';
  safePdfUrl: SafeResourceUrl | null = null;
  isLoading: boolean = false;

  constructor(private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.updatePdfUrl();
  }

  ngAfterViewInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updatePdfUrl();
  }

  private updatePdfUrl() {
    if (!this.pdfUrl) {
      this.pdfUrlWithPage = '';
      this.safePdfUrl = null;
      this.isLoading = false;
      return;
    }

    // Show loading state
    this.isLoading = true;
    this.cdr.detectChanges();

    // Create URL with page fragment for PDF navigation
    let url = this.pdfUrl;

    // Remove any existing fragment
    if (url.includes('#')) {
      url = url.split('#')[0];
    }

    // Build PDF parameters based on configuration
    let pdfParams = `page=${this.initialPage ?? 1}`;

    // Configure toolbar visibility
    if (this.hideToolbar) {
      pdfParams += '&toolbar=0'; // Hide the entire toolbar
    }

    // Configure navigation panes
    if (this.hideNavPanes) {
      pdfParams += '&navpanes=0'; // Hide side navigation panes
    }

    // Configure view mode
    if (this.fitToWidth) {
      pdfParams += '&view=FitH'; // Fit to page width
    }

    // Additional optional parameters for cleaner interface
    pdfParams += '&statusbar=0'; // Hide status bar
    pdfParams += '&messages=0'; // Hide messages
    pdfParams += '&scrollbar=1'; // Keep scrollbar for navigation

    url += `#${pdfParams}`;

    // Add timestamp to force iframe reload when page changes
    const separator = url.includes('?') ? '&' : '?';
    url += `${separator}_t=${Date.now()}`;

    this.pdfUrlWithPage = url;

    // Sanitize the URL for Angular security
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    // Show iframe after a brief delay to ensure it loads properly
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    }, 100);
  }
}
